import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TrendingUp, Globe, ShieldCheck, Zap, Star } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import api from '../utils/api';

const CATEGORIES = ['All','Electronics & Accessories','Consumer Electronics','Fashion & Apparel','Beauty & Personal Care','Home & Kitchen','Jewelry & Watches','Baby Products & Toys','Books, Music & Media','Sports & Outdoors','Automotive & Parts','Grocery & Gourmet','Health','General / Other'];
const CURRENCIES = ['All','BTC','ETH','BNB','SOL','USDT','SUI','ASTER','HYPE','XRP'];
const PLAN_COLORS = { basic:'#4DA2FF', standard:'#F5A623', pro:'#FF6B00' };

export default function Home() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [currency, setCurrency] = useState('All');
  const [prices, setPrices] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const search = searchParams.get('search') || '';

  useEffect(() => { fetchProducts(); }, [search, category, currency, page]);
  useEffect(() => {
    api.get('/prices/live').then(r => setPrices(r.data.prices || {})).catch(() => {});
    api.get('/products/featured?limit=12').then(r => setFeatured(r.data.featured || [])).catch(() => {}).finally(() => setFeaturedLoading(false));
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      if (category !== 'All') params.set('category', category);
      if (currency !== 'All') params.set('currency', currency);
      const res = await api.get(`/products?${params}`);
      setProducts(res.data.products || []);
      setTotalPages(res.data.pages || 1);
    } catch { } finally { setLoading(false); }
  };

  const COIN_COLORS = { BTC:'#F7931A', ETH:'#627EEA', BNB:'#F0B90B', SOL:'#9945FF', USDT:'#26A17B', SUI:'#4DA2FF', ASTER:'#FF6B00', HYPE:'#E040FB', XRP:'#0052FF' };

  return (
    <div className="min-h-screen pb-24">
      {/* Hero */}
      {!search && (
        <div className="relative bg-grid pt-20 pb-12 px-4 overflow-hidden">
          <div className="absolute inset-0" style={{ background:'radial-gradient(ellipse at 50% 0%, rgba(255,107,0,0.15) 0%, transparent 70%)' }} />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 text-sm font-medium"
              style={{ background:'rgba(255,107,0,0.1)', border:'1px solid rgba(255,107,0,0.3)', color:'#FF6B00' }}>
              <Zap size={14} /> The World's Crypto-Native Marketplace
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight" style={{ fontFamily:'Sora' }}>
              Shop Global.<br /><span className="gradient-text">Pay Crypto.</span>
            </h1>
            <p className="text-brand-muted text-lg mb-8 max-w-xl mx-auto">
              Buy from Africa, Asia, Europe and Americas. Pay with BTC, ETH, BNB, SOL and more. Escrow protected.
            </p>
            {/* Live prices */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {Object.entries(prices).slice(0,5).map(([coin, price]) => (
                <div key={coin} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm"
                  style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
                  <span className="font-bold" style={{ color:COIN_COLORS[coin]||'#FF6B00', fontFamily:'JetBrains Mono' }}>{coin}</span>
                  <span className="text-brand-muted font-mono text-xs">${Number(price).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-3 text-sm text-brand-muted">
              {[[ShieldCheck,'Escrow Protected'],[Globe,'Ships Worldwide'],[Zap,'Non-Custodial Wallet'],[TrendingUp,'Price-Stable Shopping']].map(([Icon,label]) => (
                <span key={label} className="flex items-center gap-1.5 px-3 py-1 rounded-full"
                  style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
                  <Icon size={13} className="text-brand-orange" /> {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 pt-6">
        {search && (
          <div className="pt-20 mb-4">
            <h2 className="text-xl font-bold" style={{ fontFamily:'Sora' }}>
              Results for "<span className="gradient-text">{search}</span>"
            </h2>
          </div>
        )}

        {/* ── FEATURED SECTION (Premium sellers only) ── */}
        {!search && !featuredLoading && featured.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Star size={16} className="text-brand-orange" fill="#FF6B00" />
                <h2 className="font-bold text-lg" style={{ fontFamily:'Sora' }}>Featured Products</h2>
                <span className="badge badge-orange text-xs">Premium Sellers</span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {featured.map(p => (
                <div key={p._id} className="relative">
                  {/* Premium badge */}
                  <div className="absolute top-2 left-2 z-10 px-1.5 py-0.5 rounded-full text-xs font-bold"
                    style={{
                      background: PLAN_COLORS[p.sellerPlan] || '#FF6B00',
                      color: 'white',
                      fontSize: '9px',
                    }}>
                    {p.sellerPlan === 'pro' ? '⭐ PRO' : p.sellerPlan === 'standard' ? '✦ STD' : '• BSC'}
                  </div>
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
            <div className="my-6 h-px" style={{ background:'linear-gradient(90deg, transparent, #1E1E2E, transparent)' }} />
          </div>
        )}

        {/* Category filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4" style={{ scrollbarWidth:'none' }}>
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => { setCategory(c); setPage(1); }}
              className="flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all"
              style={{ background:category===c?'linear-gradient(135deg,#FF6B00,#F5A623)':'#12121A', color:category===c?'white':'#6B6B8A', border:category===c?'none':'1px solid #1E1E2E', fontFamily:'DM Sans' }}>
              {c}
            </button>
          ))}
        </div>

        {/* Currency filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6" style={{ scrollbarWidth:'none' }}>
          {CURRENCIES.map(c => (
            <button key={c} onClick={() => { setCurrency(c); setPage(1); }}
              className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold transition-all"
              style={{ background:currency===c?'rgba(255,107,0,0.15)':'transparent', color:currency===c?'#FF6B00':'#6B6B8A', border:`1px solid ${currency===c?'#FF6B00':'#1E1E2E'}`, fontFamily:'JetBrains Mono' }}>
              {c}
            </button>
          ))}
        </div>

        {/* All products grid */}
        {!search && <h2 className="font-bold text-lg mb-3" style={{ fontFamily:'Sora' }}>All Products</h2>}

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden animate-pulse" style={{ background:'#12121A', border:'1px solid #1E1E2E' }}>
                <div className="aspect-square bg-brand-border" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-brand-border rounded w-3/4" />
                  <div className="h-4 bg-brand-border rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🛒</div>
            <h3 className="text-xl font-bold mb-2" style={{ fontFamily:'Sora' }}>No products found</h3>
            <p className="text-brand-muted">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {products.map(p => <ProductCard key={p._id} product={p} />)}
            </div>
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <button disabled={page===1} onClick={() => setPage(p=>p-1)} className="btn-outline px-4 py-2 text-sm disabled:opacity-30">← Prev</button>
                <span className="px-4 py-2 text-brand-muted text-sm">Page {page} of {totalPages}</span>
                <button disabled={page===totalPages} onClick={() => setPage(p=>p+1)} className="btn-outline px-4 py-2 text-sm disabled:opacity-30">Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
